import type { CallbackInterface } from "recoil";

import { type ColumnName, defaultColumns } from "Pages/RecipientsList/model";
import { fetchResultsCallback } from "Pages/RecipientsList/recoil/callbacks/callbacks";
import { reorder } from "Pages/RecipientsList/recoil/services/columns";
import { getAllColumns } from "Pages/RecipientsList/recoil/services/configColumns";
import {
  safeColumns,
  updatePagePreferences,
} from "Pages/RecipientsList/recoil/services/filters";
import {
  columnsState,
  dynamicFieldsState,
} from "Pages/RecipientsList/recoil/state/columns";
import { panelConfigColumnsState } from "Pages/RecipientsList/recoil/state/configColumns";
import { paginationState } from "Pages/RecipientsList/recoil/state/results";
import { gainsightTrack } from "mailup-react-helpers";

export const panelConfigColumnsOpenCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async () => {
    gainsightTrack("mup_recipients_click_manageColumns", {});

    const columns = await snapshot.getPromise(columnsState);
    set(panelConfigColumnsState, (prev) => ({
      ...prev,
      items: [...columns],
      show: true,
    }));
  };

export const panelConfigColumnsCloseCallback =
  ({ reset }: CallbackInterface) =>
  async () => {
    gainsightTrack("mup_recipients_click_hideRightPanel", {});
    reset(panelConfigColumnsState);
  };

export const panelConfigColumnsAddCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async (c: ColumnName | -1) => {
    if (c === -1) {
      const dynamicFields = await snapshot.getPromise(dynamicFieldsState);
      const allColumns = getAllColumns(dynamicFields);

      set(panelConfigColumnsState, ({ items, ...prev }) => ({
        ...prev,
        items: [...items, ...allColumns.filter((c) => !items.includes(c))],
      }));
    } else {
      set(panelConfigColumnsState, ({ items, ...prev }) => ({
        ...prev,
        items: [...items, c],
      }));
    }
  };

export const panelConfigColumnsAddAllCallback =
  ({ set }: CallbackInterface) =>
  async (c: ColumnName) => {
    //  TODO
    set(panelConfigColumnsState, ({ items, ...prev }) => ({
      ...prev,
      items: [...items, c],
    }));
  };

export const panelConfigColumnsRemoveCallback =
  ({ set }: CallbackInterface) =>
  async (index: number) => {
    set(panelConfigColumnsState, ({ items, ...prev }) => ({
      ...prev,
      items: items.filter((_, idx) => idx !== index),
    }));
  };

export const panelConfigColumnsReorderCallback =
  ({ set }: CallbackInterface) =>
  async (from: number, to: number) => {
    //

    set(panelConfigColumnsState, ({ items, ...prev }) => ({
      ...prev,
      items: reorder(items, from, to),
    }));
  };

export const panelConfigColumnsRestoreCallback =
  ({ set }: CallbackInterface) =>
  async () => {
    const cols = safeColumns(defaultColumns);

    set(panelConfigColumnsState, ({ items, ...prev }) => ({
      ...prev,
      items: cols,
    }));
  };

export const panelConfigColumnsConfirmCallback =
  ({ set, snapshot, reset, ...cbi }: CallbackInterface) =>
  async () => {
    const { items } = await snapshot.getPromise(panelConfigColumnsState);
    const pagination = await snapshot.getPromise(paginationState);

    const newColumns = items.filter((c) => c !== undefined) as ColumnName[];

    set(columnsState, newColumns);

    //  reset(panelConfigColumnsState)

    const orderByColumnInVisibleColumns = newColumns.includes(
      pagination.orderBy,
    );

    await updatePagePreferences({
      columns: newColumns,
    });

    await fetchResultsCallback({ set, snapshot, reset, ...cbi })({
      columns: newColumns,
      orderBy: orderByColumnInVisibleColumns ? undefined : "Id",
      orderDescending: orderByColumnInVisibleColumns ? undefined : true,
    });
  };
